import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { MarketObject, IS_3D_SHAPE, SHAPE_FOOTPRINT, DEFAULT_FOOTPRINT } from "./SelectorObjects3D";
import { SELECTOR_CATEGORIES, THEMES, getSelectorMarkets, alpha } from "./selectorMarkets";
import { fmtPrice } from "../Cockpit/cockpitMarkets";
import pokeball from "../../assets/images/Perps/pokeball.svg";
import useTheme from "../../hooks/useTheme";
import { useDisabledMarkets } from "../../hooks/useDisabledMarkets";
import { NumberTicker } from "../PerpsComponents/NumberAnimation";
import "./marketSelect.css";

/**
 * The full-screen market selector for every non-collectible category —
 * Crypto, Stocks, Commodities, Real Estate and Watches.
 *
 * LEFT plate  — a 3D coverflow of the category's market objects. Drag it, use
 *               the ‹ › arrows, the arrow keys or the dots to spin.
 * RIGHT plate — the readout for whichever market is centred: name, live price,
 *               24h change, leverage, status, description and the trade action.
 *
 * Structurally this mirrors `Cockpit/MarketSwitcher.js`, but none of the
 * Pokédex device styling comes with it: this is the app's normal light chrome.
 * The Collectibles tab is a passthrough — it hands back to that switcher.
 *
 * @param {object} props
 * @param {string} [props.currentId] - Market id to start centred on
 * @param {string} [props.initialCategory] - Category key to open on
 * @param {Object<string, {price?: number, change24h?: number}>} [props.prices] - Live prices by market id
 * @param {Function} [props.getLogo] - (market) => url|null, resolves a market's logo
 * @param {Function} props.onSelect - (marketId) => void, fired when a market is chosen
 * @param {Function} [props.onSelectCollectibles] - Hand back to the Pokédex switcher
 * @param {DOMRect} [props.anchorRect] - Viewport rect of the market button in
 *   the trading header. The overlay grows out of it on the way in and shrinks
 *   back into it on the way out, so selection reads as one continuous move
 *   rather than a page swap. Omit it and it simply fades.
 * @param {boolean} [props.enterInPlace] - True when the Pokédex switcher handed
 *   over to this rather than the trading page opening it, so it fades in where
 *   it stands instead of growing out of the market button.
 * @param {Function} props.onClose - Close the overlay
 */
/** How long the overlay takes to grow out of, or fold back into, the button. */
const COLLAPSE_MS = 380;
/**
 * The swap between this and the Pokédex switcher is not animated at all.
 *
 * Switching browsers is switching category — the same act as Real Estate to
 * Watches inside one of them, which simply redraws. Both carry the same search
 * field and tab strip in the same places, so anything played over that swap,
 * even a crossfade, shows as those shared controls flickering while the layer
 * under them changes. The objects arriving on the stage are the animation.
 */

export function MarketSelector({
  currentId,
  initialCategory,
  prices = {},
  getLogo,
  onSelect,
  onSelectCollectibles,
  anchorRect,
  enterInPlace = false,
  onClose,
}) {
  // Phones get a tighter coverflow and smaller objects — at 260px of spacing
  // the neighbours sit off-screen and the centred object overflows the stage.
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setNarrow(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // The stage's own size drives the seat pitch, the ring depth and how big the
  // objects are drawn, so it is measured rather than assumed.
  const displayRef = useRef(null);
  const [stage, setStage] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = displayRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setStage((prev) => (prev.w === width && prev.h === height ? prev : { w: width, h: height }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /**
   * How big the objects are drawn.
   *
   * They used to render at a flat 1×, tuned against a stage roughly 1180×340.
   * On the trading page's stage that left them looking squashed into the
   * middle of a much larger box — small objects, big empty floor. The scale
   * now follows whichever of the two dimensions runs out first, so the row
   * grows into the space it is given instead of sitting in the middle of it.
   *
   * Floored so a cramped stage does not shrink them into illegibility, and
   * capped because the 3D shapes are drawn at a fixed geometry: past about
   * 1.35 their edge detail starts to read as soft.
   */
  /**
   * The overlay is the trading page's market button, opened out.
   *
   * It mounts collapsed onto that button and expands into the panel; choosing a
   * market, or backing out, runs the same move in reverse and the trading page
   * is waiting underneath — still visible around the panel, because the panel
   * is a popup rather than a page. `transform-origin: 0 0` with a uniform scale
   * of `button width / panel width` puts the shrunken panel exactly over the
   * button — uniform rather than fitting both axes, because a non-uniform scale
   * would stretch every object inside it on the way.
   */
  const [shown, setShown] = useState(enterInPlace);
  const [closing, setClosing] = useState(false);
  useEffect(() => {
    if (enterInPlace) return undefined;
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
    // Only ever the mount: how it arrived cannot change under it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const open = shown && !closing;

  /**
   * The panel hangs off the market button like a dropdown rather than sitting
   * in the middle of the screen: the trader stays on the chart, and the thing
   * they clicked is still the thing the panel is attached to.
   *
   * Clamped on both axes — a button near the right edge would otherwise open a
   * 1180px panel off-screen — and below 900px it goes back to filling the
   * viewport, where there is no room to hang anything off anything.
   */
  const [vp, setVp] = useState(() => ({
    w: typeof window === "undefined" ? 1440 : window.innerWidth,
    h: typeof window === "undefined" ? 900 : window.innerHeight,
  }));
  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const box = useMemo(() => {
    const pad = 12;
    const w = Math.min(1180, vp.w - pad * 2);
    const small = vp.w < 900;
    if (!anchorRect || small) {
      const h = Math.min(760, vp.h - pad * 2);
      return { left: Math.round((vp.w - w) / 2), top: Math.round(small ? pad : (vp.h - h) / 2), width: w, height: h };
    }
    // Never so low that the panel has no room left under it.
    const top = Math.round(Math.min(anchorRect.bottom + 10, Math.max(64, vp.h - 320)));

    /*
     * The panel is the chart column, opened out — it takes that column's exact
     * left edge and exact width, and never reaches the order rail.
     *
     * Every trading page is the same row: `w-[92%] max-w-[1600px] mx-auto`,
     * `gap-5`, a fluid left column and a fixed right rail (340px, 400px from
     * xl) holding Buy/Long, the deposit field and the leverage dial. That rail
     * is the one module a trader may be part-way through when they reach for
     * the market button, so the panel stops short of it — and having stopped,
     * it may as well start where the column starts. Hanging off the button's
     * own left edge instead left a strip of page showing down the left side
     * that read as misalignment rather than as a dropdown.
     *
     * Derived from those layout constants rather than measured: the panel is
     * portalled out of the page and has no handle on either column. The floor
     * is what the coverflow plus the list column need to stay usable; the
     * <900px branch above takes over long before it binds.
     */
    const rail = vp.w >= 1280 ? 400 : 340;
    const row = Math.min(1600, vp.w * 0.92);
    const gutter = (vp.w - row) / 2;
    const left = Math.round(Math.max(pad, gutter));
    const width = Math.max(660, Math.min(w, Math.round(row - rail - 20) /* gap-5 */));

    return { left, top, width, height: Math.min(720, vp.h - top - pad) };
  }, [anchorRect, vp]);

  /**
   * The panel is pinned to the page, not to the screen.
   *
   * It renders in a `position: fixed` backdrop, so it used to ride down the
   * viewport with the trader as they scrolled the chart column — a dropdown
   * that follows you is a panel that has come loose from the button it belongs
   * to. It now offsets by however far its scroller has moved since it opened,
   * which leaves it parked under the market button and lets the trader scroll
   * it out of the way without dismissing it.
   *
   * The scrollers are found by hit-testing the button's own position:
   * `elementsFromPoint` hands back the stack under it, so whichever of those
   * actually scrolls (the trading page scrolls its left column, not the
   * window) is the one this follows. Baselines are taken at mount, before any
   * of them have moved.
   */
  const [scrolled, setScrolled] = useState(0);
  useEffect(() => {
    if (!anchorRect) return undefined;
    const x = Math.min(vp.w - 1, Math.max(0, anchorRect.left + anchorRect.width / 2));
    const y = Math.min(vp.h - 1, Math.max(0, anchorRect.top + anchorRect.height / 2));
    const base = new Map();
    for (const el of document.elementsFromPoint(x, y)) {
      if (el.scrollHeight > el.clientHeight + 1) base.set(el, el.scrollTop);
    }
    const baseWin = window.scrollY;
    const onScroll = () => {
      let d = window.scrollY - baseWin;
      base.forEach((from, el) => { d += el.scrollTop - from; });
      setScrolled((prev) => (prev === d ? prev : d));
    };
    // Capture, because scroll does not bubble — the left column's own scroll
    // never reaches the document otherwise.
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("scroll", onScroll);
    };
    // The anchor is fixed for the life of the panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorRect]);

  /**
   * Where the panel actually landed, so the collapsed pose can be expressed
   * relative to it. Read off the *layout* box — `offsetLeft/Top/Width`, not
   * `getBoundingClientRect()` — because the panel is already carrying the
   * collapsed transform on the frame this runs, and the measured rect would
   * fold that transform back into itself. The panel's offset parent is the
   * fixed backdrop pinned to 0,0, so these are viewport coordinates.
   */
  const panelRef = useRef(null);
  const [collapsed, setCollapsed] = useState("scale(.96)");
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!anchorRect || !el || !el.offsetWidth) return;
    const s = anchorRect.width / el.offsetWidth;
    setCollapsed(
      `translate(${anchorRect.left - el.offsetLeft}px, ${anchorRect.top - el.offsetTop}px) scale(${s})`
    );
  }, [anchorRect]);
  // In place, the rest state is barely off — enough to read as a layer
  // arriving or leaving, not as travel.

  /**
   * Close on a delay, so the move is seen before the overlay unmounts.
   * Everything that dismisses this — ✕, Escape, picking a market, handing over
   * to the Pokédex — goes through here rather than calling the host directly.
   *
   * @param {Function} done - What to do once the move has played
   * @param {boolean} [handoff] - Leaving for the other browser, not the page
   */
  const closeAfterCollapse = useCallback((done, handoff = false) => {
    if (handoff) { done(); return; }
    setClosing(true);
    window.setTimeout(done, COLLAPSE_MS);
  }, []);
  const requestClose = useCallback(() => closeAfterCollapse(onClose), [closeAfterCollapse, onClose]);

  /**
   * Dismiss on a click outside the panel.
   *
   * The backdrop used to do this by being clickable, but it now passes events
   * through so the page underneath stays scrollable. Three things are not
   * "outside": the flat market list, which renders over this panel rather than
   * inside it; the market button, whose own handler owns what a click on it
   * means; and anything the host has marked as part of this control.
   */
  useEffect(() => {
    const onDocDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (e.target.closest?.("[data-market-dropdown], .ce-market-btn, .ce-dex-backdrop")) return;
      // Swallow the click this mousedown is about to become, so dismissing the
      // panel does not also press whatever was underneath it.
      const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
      window.addEventListener("click", swallow, { capture: true, once: true });
      window.setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 0);
      requestClose();
    };
    document.addEventListener("mousedown", onDocDown, true);
    return () => document.removeEventListener("mousedown", onDocDown, true);
  }, [requestClose]);

  const objScale = useMemo(() => {
    if (narrow) return 0.64;
    if (!stage.w || !stage.h) return 1;
    const byWidth = stage.w / 1180;
    // The tallest shapes are ~320px; the margin keeps the centred object, which
    // is drawn at 1.04×, clear of the stage floor.
    const byHeight = (stage.h - 40) / 320;
    // The floor used to be 0.85, tuned when the stage owned most of a screen.
    // As a dropdown the stage can be half that tall, and a floor above what
    // fits does not protect the objects — it just cuts their feet off at the
    // bottom edge. Small enough to read is better than clipped.
    return Math.max(0.5, Math.min(1.35, Math.min(byWidth, byHeight)));
  }, [narrow, stage]);

  // Open on the category the trader is already in, when it is one we cover.
  const [cat, setCat] = useState(() => {
    const start = getSelectorMarkets("all").find((m) => m.id === currentId);
    return initialCategory || start?.category || "crypto";
  });
  const [query, setQuery] = useState("");
  const [drag, setDrag] = useState(false);

  const theme = THEMES[cat] || THEMES.all;
  const A = theme.accent;

  // Everything themed reads `var(--token)`; `isDark` exists only for the two
  // accent-wash opacities and the wordmark filter, which have no token form.
  const { isDark } = useTheme();
  // Disabled markets stay selectable (traders still need to close positions)
  // but are labelled so nobody picks one expecting to open a trade.
  const isMarketDisabled = useDisabledMarkets();

  /**
   * The markets on the stage: the current category, or — while there is a
   * search term — every category the selector covers.
   *
   * Search is global on purpose. Scoped to the active tab, looking up a ticker
   * meant knowing which tab it lived under first, and a search that returns
   * nothing while the market is one tab away reads as "we don't list it".
   */
  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = getSelectorMarkets(q ? "all" : cat).map((m) => {
      const p = prices[m.id] || {};
      return {
        ...m,
        price: p.price != null ? Number(p.price) : null,
        change: p.change24h != null ? Number(p.change24h) : 0,
      };
    });
    if (!q) return base;
    return base.filter((m) => `${m.name} ${m.short} ${m.id}`.toLowerCase().includes(q));
  }, [cat, prices, query]);

  /**
   * Seat pitch.
   *
   * 190 is the shipped value (ADDENDUM-7 §5) — at 260 the solids sat too far
   * apart to read as one row once they gained real depth. But it was tuned on
   * the coin, whose wrapper is 192px wide, and it is a *flat* pitch: a 232px
   * certificate or a 230px tower at the same pitch lands half on top of its
   * neighbour, which is the overlap you see on Stocks and Real Estate.
   *
   * The pitch now follows the widest object actually on screen, with 190 as the
   * floor. `footprint - 2` reproduces the tuned relationship exactly, so coins
   * (192 → 190) and watches (186 → 190) render at the shipped spacing and only
   * the genuinely wider shapes get more room.
   *
   * On phones the objects are scaled to 0.64, so their footprints shrink below
   * the 150 floor and mobile spacing is unchanged.
   */
  const spacing = useMemo(() => {
    const widest = list.reduce(
      (w, m) => Math.max(w, SHAPE_FOOTPRINT[m.shape] ?? DEFAULT_FOOTPRINT),
      0
    );
    const floor = narrow ? 150 : 190;
    const base = Math.max(floor, (widest - 2) * objScale);
    // Never more than a third of the stage. The floor was written for a stage
    // wide enough to hold three seats; on a phone it is wider than the stage
    // itself, which is what pushed the neighbours off both ends and left them
    // clipped by the frame with the arrows sitting on top of them.
    return stage.w ? Math.min(base, stage.w * 0.34) : base;
  }, [list, narrow, objScale, stage.w]);

  const startIdx = useMemo(() => {
    const i = list.findIndex((m) => m.id === currentId);
    return i < 0 ? 0 : i;
    // Only when the category changes — spinning back on every price tick would
    // yank the coverflow out from under the trader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat]);

  const [pos, setPos] = useState(startIdx);
  const posRef = useRef(pos);
  posRef.current = pos;

  useEffect(() => { setPos(startIdx); }, [startIdx]);

  // Typing re-centres on the first result, but the first render must keep the
  // trader's current market centred rather than snapping to index 0.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    setPos(0);
  }, [query]);

  /**
   * The row is a ring, not a line.
   *
   * Seats used to be laid out at `i - pos`, which left the stage empty on
   * whichever side you had run out of markets — at the first market there was
   * nothing to its left at all. The list now closes on itself: the markets at
   * the end of it sit to the left of the first one, and spinning either way
   * goes round for ever rather than hitting a wall.
   *
   * Under three markets there is nothing to close — one neighbour cannot sit
   * on both sides at once without being drawn twice — so those fall back to
   * the old clamped line.
   */
  const n = list.length;
  const wraps = n >= 3;

  /**
   * How many seats out from centre are drawn.
   *
   * Enough to reach both ends of the stage, floored at the shipped three so a
   * narrow stage still reads as a row. The ceiling is low on purpose: seats
   * translate back in z as well as across, so the perspective divide pulls the
   * far ones toward the middle — past four or so they stack up near the centre
   * instead of extending the row, and cost a render each to do it. Width is
   * capped on the plate instead. It can never exceed half the ring either: a
   * seat at exactly half a turn is where its offset flips sides, which on
   * screen reads as a teleport.
   */
  const cull = useMemo(() => {
    const reach = stage.w ? Math.ceil(stage.w / 2 / spacing) + 0.6 : 3.4;
    const depth = Math.max(3.4, Math.min(4.4, reach));
    return wraps ? Math.min(depth, n / 2 - 0.05) : depth;
  }, [stage.w, spacing, wraps, n]);
  // Seats fade out a little inside the cull, so they are already invisible by
  // the time they unmount and the transition never shows the cut.
  const fadeAt = cull - 0.8;

  /** Signed distance from `p` to seat `i`, the short way round the ring. */
  const delta = useCallback(
    (i, p) => {
      const raw = i - p;
      return wraps ? raw - n * Math.round(raw / n) : raw;
    },
    [n, wraps]
  );

  // `pos` runs free once the ring closes — it is allowed past either end and
  // keeps counting, which is what makes the spin continuous. Only the seat it
  // settles on is folded back into the list.
  const clamp = useCallback(
    (p) => (wraps ? p : Math.max(0, Math.min(n - 1, p))),
    [wraps, n]
  );
  const center = useMemo(() => {
    if (!n) return 0;
    const r = Math.round(clamp(pos)) % n;
    return r < 0 ? r + n : r;
  }, [pos, n, clamp]);
  const cm = list[center] || null;

  const nudge = useCallback((d) => setPos((p) => clamp(Math.round(p) + d)), [clamp]);

  /** Spin to seat `i` the short way, rather than unwinding the whole ring. */
  const goTo = useCallback((i) => setPos((p) => clamp(p + delta(i, p))), [clamp, delta]);

  /**
   * Scrub straight to the seat under the pointer.
   *
   * Deliberately NOT `goTo`. That takes the short way round the ring, so from
   * the second market a click on the right-hand end of the track spun the
   * carousel backwards — the slider and the objects moving opposite ways. The
   * track is a line, so dragging right has to travel right, however far round
   * the ring that is.
   */
  const scrubTo = useCallback((e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    setPos(Math.round(ratio * (n - 1)));
  }, [n]);

  const choose = useCallback(
    (id) => { if (id) closeAfterCollapse(() => onSelect(id)); },
    [closeAfterCollapse, onSelect]
  );

  const onDown = (e) => {
    setDrag(true);
    const startX = e.clientX;
    const startPos = clamp(posRef.current);
    const move = (ev) => setPos(clamp(startPos - (ev.clientX - startX) / spacing));
    const up = () => {
      setDrag(false);
      setPos((p) => clamp(Math.round(p)));
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  useEffect(() => {
    const onKey = (e) => {
      // The list overlays this plate and runs its own arrow/Enter/Escape
      // handling. Spinning the coverflow underneath it — or closing the whole
      // overlay on the Escape that was meant to dismiss the list — is never
      // what the keypress meant.
      if (e.key === "ArrowRight") nudge(1);
      else if (e.key === "ArrowLeft") nudge(-1);
      else if (e.key === "Enter" && cm) choose(cm.id);
      else if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nudge, choose, cm, requestClose]);

  const pickCategory = (key) => {
    // The pokeball loader that covers this swap is mounted by the host, so it
    // survives this component unmounting. It only plays once a session now, so
    // the fade underneath it has to stand on its own the rest of the time.
    if (key === "collectibles") { closeAfterCollapse(() => onSelectCollectibles?.(), true); return; }
    setCat(key);
    setQuery("");
    setPos(0);
  };

  const logoFor = (m) => (getLogo ? getLogo(m) : (/^https?:\/\//.test(m.iconPath || "") ? m.iconPath : null));

  const plate = {
    background: "var(--surface)", border: 0, borderRadius: 18,
    boxShadow: "var(--card)", padding: 12,
    display: "flex", flexDirection: "column", gap: 8, minWidth: 0, minHeight: 0,
    overflow: "hidden",
  };

  // `ce-raw` on the shell opts the whole subtree out of cockpit.css's sweeps.
  // The card markets keep `ce-cockpit-active` on <body> while this overlay is
  // open, and without it the Pokédex chrome — 3px black outlines, hard offset
  // shadows, red accents — repaints a selector that is not a Pokédex.
  return (
    <div
      className="ce-ms-backdrop ce-raw"
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        // Scrolling the page slides the panel up out of view, and at z-300 it
        // would ride straight over the app header on the way. Clipped to the
        // area below it so it passes under the header instead — a clip rather
        // than a lower z-index, which would also put it under every plate on
        // the trading page.
        clipPath: "inset(var(--app-header-h, 68px) 0 0 0)",
        // Not a scrim. `pointer-events: none` and no tint, because the page
        // behind stays live: with the backdrop swallowing events a wheel over
        // the chart did nothing, and the trading page could not be scrolled
        // while the panel was open. The panel scrolls its own list, the page
        // scrolls itself, and dismissal moves to the document listener below.
        pointerEvents: "none",
      }}
      aria-hidden="true"
    >
      <div
        ref={panelRef}
        // `ce-raw` opts the panel out of cockpit.css's sweeps, and `theme.css`
        // keys the dark override off `.ce-ms-shell.ce-raw` — the panel is app
        // chrome even when it opens over the Pokédex page, whose token reset it
        // would otherwise inherit. Both classes have to stay on this element.
        className="ce-ms-shell ce-raw"
        style={{
          position: "absolute",
          left: box.left, top: box.top - scrolled, width: box.width, height: box.height,
          // Tinted off the active category so each index feels distinct. The two
          // wash opacities are the one numeric conditional the theme keeps
          // (ADDENDUM-8 §2): at light-mode strength the wash disappears entirely
          // against a near-black page.
          background: `radial-gradient(120% 80% at 12% -8%, ${alpha(A, isDark ? 0.17 : 0.1)} 0%, transparent 58%), radial-gradient(90% 70% at 100% 106%, ${alpha(A, isDark ? 0.13 : 0.07)} 0%, transparent 62%), var(--bg)`,
          border: 0,
          boxShadow: "var(--float)",
          overflow: "hidden", color: "var(--ink)", fontFamily: "General Sans",
          transformOrigin: "0 0",
          transform: open ? "none" : collapsed,
          opacity: open ? 1 : 0,
          // Tighter while collapsed so it reads as the button it is folding
          // into, and a panel corner once it is open.
          borderRadius: open ? 24 : 16,
          transition: `transform ${COLLAPSE_MS}ms cubic-bezier(.4,0,.2,1), opacity ${Math.round(COLLAPSE_MS * 0.6)}ms linear, border-radius ${COLLAPSE_MS}ms linear`,
          willChange: "transform",
          // The one live layer inside a backdrop that no longer takes events.
          pointerEvents: "auto",
        }}
      >
      <div
        className="ce-ms-page"
        style={{
          position: "relative", zIndex: 1, maxWidth: 1360, height: "100%", margin: "0 auto",
          padding: "16px 18px 14px", display: "flex", flexDirection: "column", gap: 12, minHeight: 0,
          // The panel is only as tall as the space under the market button, so
          // every row in this column has to live inside it. Without the clip a
          // short viewport printed the ticker rail across the middle of the
          // stage and ran the objects out through the bottom edge.
          overflow: "hidden",
        }}
      >
        {/* ── Search + category tabs ── */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, flexShrink: 0 }}>
          {/* Search is the "I know what I want, skip the spinning" path: it
              filters this overlay's own coverflow and list column. The flat
              market list that used to open over it is gone. */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, width: "min(680px,100%)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, flex: "1 1 auto", minWidth: 0, height: 42, padding: "0 16px", background: "var(--surface-2)", border: 0, borderRadius: 999, boxShadow: "var(--inset)" }}>
            <span style={{ color: "var(--ink-3)", fontSize: 14 }} aria-hidden="true">⌕</span>
            <input
              value={query}
              // Typing widens the search to every market. Searching inside one
              // category means "MSFT" finds nothing while standing on Real
              // Estate, which reads as the market not existing rather than as
              // the filter hiding it. The tab strip and the plate headings
              // follow, so the panel says what it is actually searching.
              onChange={(e) => {
                const v = e.target.value;
                if (v && cat !== "all") { setCat("all"); setPos(0); }
                setQuery(v);
              }}
              placeholder="Search markets — name or ticker"
              aria-label="Search markets"
              style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontSize: 14, fontWeight: 500, color: "var(--ink)" }}
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                aria-label="Clear search"
                style={{ width: 20, height: 20, borderRadius: "50%", border: "none", background: "var(--surface-3)", color: "var(--ink-2)", cursor: "pointer", fontSize: 10, lineHeight: 1, flexShrink: 0 }}
              >
                ✕
              </button>
            )}
          </div>


          {/* No ✕ and no theme toggle. The header they lived in is gone, and a
              dropdown is dismissed the way dropdowns are dismissed: Escape, a
              click outside it, or the market button that opened it. The theme
              toggle belongs to the page, which is still right there. */}
          </div>

          {/* `width: 100%` is load-bearing — the centred column would otherwise
              size this row to max-content and it would never wrap. */}
          {/* One sunken rail holding one raised tab, rather than seven pills
              each carrying their own border. The active tab's *label* takes
              that category's accent — so the tabs preview where you are going
              rather than restating where you are. */}
          <div
            className="ce-ms-tabs"
            style={{
              display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 4, width: "100%",
              padding: 4, borderRadius: 999,
              background: "var(--surface-2)", boxShadow: "var(--inset-sm)",
            }}
          >
            {SELECTOR_CATEGORIES.map((t) => {
              const on = t.key === cat;
              // Each tab's own colour, not the active category's.
              const tint = (THEMES[t.key] || THEMES.all).accent;
              return (
                <button
                  key={t.key}
                  className="ce-ms-lift ce-ms-tab"
                  onClick={() => pickCategory(t.key)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 7,
                    flex: "1 1 0", justifyContent: "center", minWidth: 0,
                    padding: "8px 14px", borderRadius: 999, cursor: on ? "default" : "pointer",
                    // A label that wraps is a label that has already overflowed
                    // its pill — the strip drops to content-width tabs on
                    // narrow rails instead (marketSelect.css).
                    whiteSpace: "nowrap",
                    border: 0,
                    background: on ? "var(--raise)" : "transparent",
                    color: on ? tint : "var(--ink-3)",
                    fontSize: 13.5, fontWeight: on ? 600 : 500,
                    boxShadow: on ? "var(--pillow-sm)" : "none",
                  }}
                >
                  {t.key === "collectibles" && (
                    <img src={pokeball} alt="" style={{ width: 15, height: 15, display: "block", flexShrink: 0 }} />
                  )}
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── The stage ──
            The readout plate that used to sit beside this is gone. Browsing is
            the job here, and a column of price, 24h, leverage and status
            duplicated the trading page the selector hands off to. The objects
            get the whole width instead. */}
        {/* 380px for the list, up from 320: the stage was running wider than
            the coverflow can use — the seats bunch toward the middle under the
            perspective divide, so the extra width was empty floor either side
            of the objects. The list spends it on room to breathe instead. */}
        {/* Now that the panel stops at the order rail there is no surplus left
            to spend, so under 1000px the list hands 60px back to the stage. */}
        <div className="ce-ms-body" style={{ display: "grid", gridTemplateColumns: `minmax(0, 1fr) ${box.width < 1000 ? 320 : 380}px`, gap: 14, alignItems: "stretch", justifyItems: "stretch", flex: "1 1 auto", minHeight: 0 }}>
          {/* Capped rather than full-bleed. The coverflow's seats translate
              back in z, so the perspective divide bunches them toward the
              middle, so past a point extra width buys empty floor rather than
              more row. The cap is where that trade stops paying — the objects
              scale up to meet it rather than the stage growing past it. */}
          {/* `height: 100%` is load-bearing, not decoration. Sized by its
              content instead, the plate could come out taller than the row it
              sits in — and centred in that row, the excess spilled evenly top
              and bottom and printed over the category tabs above. Bounded to
              the space it is given, it cannot. */}
          <div className="ce-ms-plate-left" style={{ ...plate, width: "100%", minWidth: 0, height: "100%", maxHeight: 720 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexShrink: 0, padding: "0 4px" }}>
              {/* The two side columns carry equal weight so the name between
                  them lands on the plate's centre line. Sized by content, the
                  eyebrow and the counter differ by tens of pixels and the name
                  centres in the leftover space instead — visibly off to one
                  side of everything it labels. */}
              <span style={{ flex: "1 1 0", display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                {/* Decorative rhythm, not status lamps — these never animate. */}
                <span style={{ display: "flex", gap: 4 }} aria-hidden="true">
                  {[1, 0.45, 0.2].map((o) => (
                    <i key={o} style={{ width: 6, height: 6, borderRadius: "50%", background: o === 1 ? A : alpha(A, o), display: "block" }} />
                  ))}
                </span>
                <span className="ce-ms-plate-label" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11, fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--ink-3)" }}>{theme.cat}</span>
              </span>
              {/* The centred market's name.
                  The objects carry a logo and a ticker at most, and the
                  readout that used to name them is gone — without this the
                  thing you are about to click is identified only by its
                  artwork. It sits in the plate's header rather than over the
                  stage: as a popup the stage is short enough that a tall
                  object — the Real Estate towers, a card — reaches the top
                  band the name used to own, and printed straight through it. */}
              <span
                className="ce-ms-stage-name"
                style={{
                  flex: "0 1 auto", minWidth: 0, textAlign: "center",
                  fontSize: 20, fontWeight: 600, letterSpacing: "-.025em", lineHeight: 1.2,
                  color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}
              >
                {cm ? cm.name : ""}
              </span>
              <span style={{ flex: "1 1 0", textAlign: "right", fontSize: 11.5, fontWeight: 600, color: "var(--ink-2)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                {list.length ? center + 1 : 0} of {list.length}
              </span>
            </div>

            <div
              className="ce-ms-display"
              ref={displayRef}
              onPointerDown={onDown}
              style={{
                // A floor low enough that it never fights the plate for room:
                // on a short viewport the objects scale down to fit the stage
                // rather than the stage growing to fit the objects.
                position: "relative", flex: "1 1 auto", minHeight: 140,
                background: "var(--display)",
                border: "1px solid var(--line-2)", borderRadius: 12, overflow: "hidden",
                perspective: 1500, touchAction: "none", cursor: drag ? "grabbing" : "grab",
                boxShadow: "inset 0 1px 3px rgba(16,24,40,.06)",
              }}
            >
              {/* Accent wash — ties the stage to the active category. */}
              <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(70% 60% at 50% 44%, ${alpha(A, 0.09)} 0%, transparent 70%)` }} aria-hidden="true" />

              {/* Stage furniture — grid, watermark, horizon and pedestal. This
                  is what gives the centred object something to stand on, the
                  job the device plate did in the Pokédex switcher. */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.5,
                  backgroundImage: `linear-gradient(${alpha(A, 0.1)} 1px, transparent 1px), linear-gradient(90deg, ${alpha(A, 0.1)} 1px, transparent 1px)`,
                  backgroundSize: "34px 34px",
                  maskImage: "radial-gradient(80% 70% at 50% 45%, #000 20%, transparent 78%)",
                  WebkitMaskImage: "radial-gradient(80% 70% at 50% 45%, #000 20%, transparent 78%)",
                }}
              />
              <div
                aria-hidden="true"
                className="ce-ms-watermark"
                style={{
                  position: "absolute", left: 0, right: 0, top: "50%", transform: "translateY(-50%)",
                  textAlign: "center", pointerEvents: "none", userSelect: "none", lineHeight: 1,
                  fontSize: 108, fontWeight: 600, letterSpacing: "-.04em", textTransform: "uppercase",
                  color: alpha(A, 0.05), whiteSpace: "nowrap", overflow: "hidden",
                }}
              >
                {theme.cat}
              </div>
              <div aria-hidden="true" style={{ position: "absolute", left: "8%", right: "8%", bottom: "26%", height: 1, pointerEvents: "none", background: `linear-gradient(90deg, transparent, ${alpha(A, 0.28)} 22%, ${alpha(A, 0.28)} 78%, transparent)` }} />
              <div aria-hidden="true" style={{ position: "absolute", left: "50%", bottom: "22%", width: 300, height: 44, marginLeft: -150, borderRadius: "50%", pointerEvents: "none", filter: "blur(3px)", background: `radial-gradient(closest-side, ${alpha(A, 0.3)} 0%, ${alpha(A, 0.1)} 52%, transparent 78%)` }} />

              <div style={{ position: "absolute", inset: 0, transformStyle: "preserve-3d" }}>
                {list.map((m, i) => {
                  const off = delta(i, pos);
                  const abs = Math.abs(off);
                  // A seat's offset flips sign at half a turn — the far side of
                  // the ring is reachable both ways. Culling short of that
                  // keeps the flip off screen, where it would otherwise read as
                  // an object teleporting across the stage mid-transition.
                  if (abs > cull) return null;
                  const isCenter = center === i;
                  const is3D = IS_3D_SHAPE.has(m.shape);
                  return (
                    <div
                      key={m.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isCenter) choose(m.id);
                        else goTo(i);
                      }}
                      style={{
                        position: "absolute", left: "50%", top: "50%", cursor: "pointer",
                        // The solids are real geometry in this stage's space, so
                        // the wrapper has to keep 3D alive down to their faces.
                        transformStyle: "preserve-3d",
                        transform: `translate(-50%,-50%) translateX(${off * spacing}px) translateZ(${-abs * 220}px) rotateY(${Math.max(-52, Math.min(52, off * -30))}deg) scale(${(isCenter ? 1.04 : 0.9) * objScale})`,
                        transition: drag ? "none" : "transform .45s cubic-bezier(.22,1,.36,1), opacity .45s, filter .45s",
                        zIndex: 100 - Math.round(abs),
                        // ADDENDUM-9. Depth is scale, rotation, z-translation and
                        // a small brightness falloff — never opacity, and never a
                        // tint on the material itself.
                        //
                        // Off-centre solids used to render at 55% opacity, which
                        // composited them more than half into the stage: alpha
                        // blending toward a light neutral IS desaturation, so gold
                        // read as pale grey until it reached the centre seat.
                        //
                        // `brightness` falls 7% per seat, clamped at two seats so
                        // the third — already at the cull boundary — does not go
                        // darker than the second. `saturate` goes *up* slightly:
                        // dropping luminance drops perceived colourfulness (the
                        // Hunt effect), and without this the same metal reads as
                        // two different metals across seats.
                        //
                        // Flat tiles keep the original fade. They are printed
                        // artwork rather than lit material, so fading is correct.
                        opacity: abs > fadeAt ? 0 : 1,
                        filter: isCenter
                          ? "none"
                          : is3D
                            ? `saturate(1.04) brightness(${(1 - Math.min(abs, 2) * 0.07).toFixed(3)}) contrast(.96)`
                            : "saturate(.55) brightness(1.05) opacity(.5)",
                      }}
                    >
                      <MarketObject m={m} logo={logoFor(m)} />
                    </div>
                  );
                })}
              </div>

              {/* The "no matches" line used to live in the readout plate. With
                  that gone the stage has to say it, or a search that matches
                  nothing reads as an empty room. */}
              {list.length === 0 && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 24px", textAlign: "center", color: "var(--ink-4)", fontWeight: 500, fontSize: 14, pointerEvents: "none" }}>
                  {/* Search spans every category, so naming the active tab
                      here would misreport what was actually searched. */}
                  No markets match “{query}”.
                </div>
              )}

              {list.length > 1 && (
                <>
                  <button
                    className="ce-ms-lift ce-ms-arrow"
                    onClick={(e) => { e.stopPropagation(); nudge(-1); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    aria-label="Previous market"
                    style={{ position: "absolute", top: "50%", left: 16, marginTop: -19, width: 38, height: 38, borderRadius: "50%", border: 0, background: "var(--raise)", boxShadow: "var(--pillow-sm)", color: "var(--ink)", fontSize: 17, lineHeight: 1, cursor: "pointer", zIndex: 200 }}
                  >
                    ‹
                  </button>
                  <button
                    className="ce-ms-lift ce-ms-arrow"
                    onClick={(e) => { e.stopPropagation(); nudge(1); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    aria-label="Next market"
                    style={{ position: "absolute", top: "50%", right: 16, marginTop: -19, width: 38, height: 38, borderRadius: "50%", border: 0, background: "var(--raise)", boxShadow: "var(--pillow-sm)", color: "var(--ink)", fontSize: 17, lineHeight: 1, cursor: "pointer", zIndex: 200 }}
                  >
                    ›
                  </button>
                </>
              )}

            </div>

            {/* Under the stage rather than floating inside it. Parked over the
                floor it sat across the centred object, which is the tallest
                thing on screen and the one you are being asked to look at.

                A dot per market stopped being a position indicator at about a
                dozen — 32 of them is a texture, not something you can aim at.
                Long lists get a scrubber you can drop a finger anywhere on;
                short ones keep the dots, which are easier to hit. */}
            <div className="ce-ms-dots" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, flexShrink: 0, height: 18 }}>
              {n > 12 ? (
                <div
                  role="slider"
                  aria-label="Market position"
                  aria-valuemin={1}
                  aria-valuemax={n}
                  aria-valuenow={center + 1}
                  // Grab it and scrub, not just click. `setPointerCapture`
                  // keeps the drag alive past the edges of a 4px track.
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    e.currentTarget.setPointerCapture(e.pointerId);
                    scrubTo(e);
                  }}
                  onPointerMove={(e) => { if (e.currentTarget.hasPointerCapture(e.pointerId)) scrubTo(e); }}
                  style={{ position: "relative", width: "min(420px, 62%)", height: 6, borderRadius: 999, background: "var(--surface-2)", boxShadow: "var(--inset-sm)", cursor: "pointer", touchAction: "none" }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute", top: -3, height: 10, width: 34, borderRadius: 999, background: A,
                      // Inset by the thumb's own width so it stays inside the
                      // track at both ends instead of hanging off them.
                      left: `calc(${n > 1 ? (center / (n - 1)) * 100 : 0}% - ${(center / Math.max(1, n - 1)) * 34}px)`,
                      transition: "left .28s cubic-bezier(.22,1,.36,1)",
                      boxShadow: `0 0 10px ${alpha(A, 0.5)}`,
                    }}
                  />
                </div>
                ) : (
                list.map((m, i) => (
                  <span
                    key={m.id}
                    onClick={(e) => { e.stopPropagation(); goTo(i); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    role="button"
                    aria-label={`Show ${m.name}`}
                    style={{ width: i === center ? 20 : 6, height: 6, borderRadius: 999, background: i === center ? A : "var(--line-3)", cursor: "pointer", display: "block", transition: "width .2s, background .2s" }}
                  />
              ))
              )}
            </div>
          </div>

          {/* The same markets as a list, beside the stage.
              The coverflow is for looking; this is for finding. One spins to
              whatever the pointer is over, so hovering the list previews it on
              the stage in full size, and clicking trades it — which makes the
              list a fast path without taking the browse away from anyone who
              wants it. It is the first thing dropped when the panel narrows. */}
          <div className="ce-ms-listcol" style={{ ...plate, padding: 10, gap: 8, height: "100%", maxHeight: 720 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexShrink: 0, padding: "2px 6px 0" }}>
              <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--ink-3)" }}>
                {theme.cat}
              </span>
              <span style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-4)", fontVariantNumeric: "tabular-nums" }}>
                {n} {n === 1 ? "market" : "markets"}
              </span>
            </div>

            <div className="ce-ms-scroll" style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, padding: "2px 2px 4px" }}>
              {list.map((m, i) => {
                const on = i === center;
                const chg = Number(m.change || 0);
                const logo = logoFor(m);
                return (
                  <button
                    key={m.id}
                    type="button"
                    className="ce-ms-row"
                    // Hover previews on the stage; the click is the commitment.
                    onMouseEnter={() => goTo(i)}
                    onClick={() => choose(m.id)}
                    aria-current={on ? "true" : undefined}
                    /* Raised pill-cards, the same object as the positions
                       table's rows at a smaller size. The current market is
                       the one exception to "no fill and a border at once": it
                       needs to be findable in a scrolled list, and an accent
                       wash plus an accent hairline is how it says so. */
                    style={{
                      display: "flex", alignItems: "center", gap: 9, width: "100%", textAlign: "left",
                      padding: "7px 9px", borderRadius: 16, cursor: "pointer",
                      background: on ? alpha(A, 0.1) : "var(--raise)",
                      border: 0,
                      boxShadow: on ? `inset 0 0 0 1px ${A}` : "var(--pillow-sm)",
                    }}
                  >
                    <span className="ce-logo-tile" style={{ width: 26, height: 26, borderRadius: "50%" }}>
                      {logo ? (
                        <img src={logo} alt="" style={{ width: 16, height: 16, objectFit: "contain", display: "block" }} />
                      ) : (
                        <span style={{ fontSize: 9, fontWeight: 700, color: "var(--ink-3)" }}>{(m.short || "").slice(0, 3)}</span>
                      )}
                    </span>

                    <span style={{ minWidth: 0, flex: "1 1 auto" }}>
                      <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {m.short}
                        {isMarketDisabled(m) && (
                          <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 999, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", color: "var(--ink-3)", background: "var(--raise-2, rgba(127,127,127,.14))" }}>
                            Disabled
                          </span>
                        )}
                      </span>
                      <span style={{ display: "block", fontSize: 11, fontWeight: 500, color: "var(--ink-4)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {m.name}
                      </span>
                    </span>

                    <span style={{ flexShrink: 0, textAlign: "right" }}>
                      <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--ink-2)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        <NumberTicker text={fmtPrice(m.price)} duration={0.5} stagger={0.025} />
                      </span>
                      <span style={{ display: "block", fontSize: 11, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: chg >= 0 ? "var(--up)" : "var(--down)" }}>
                        <NumberTicker text={`${chg >= 0 ? "+" : ""}${chg.toFixed(2)}%`} duration={0.5} stagger={0.025} />
                      </span>
                    </span>
                  </button>
                );
              })}

              {list.length === 0 && (
                <div style={{ padding: "18px 10px", textAlign: "center", fontSize: 12.5, fontWeight: 500, color: "var(--ink-4)" }}>
                  Nothing matches “{query}”.
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Hint line. The ticker rail that used to run above it is gone: the
            list column beside the stage is the same markets and the same
            prices, standing still instead of sliding past, and a dropdown has
            no height to spend saying a thing twice. */}
        <div className="ce-ms-hint" style={{ display: "flex", flexDirection: "column", gap: 9, flexShrink: 0 }}>
          <span style={{ textAlign: "center", fontSize: 12, fontWeight: 500, color: "var(--ink-3)" }}>
            Drag to spin · ‹ › or arrow keys · click the centred market to trade
          </span>
        </div>
      </div>

      </div>
    </div>
  );
}

export default MarketSelector;
